import {
  Grid,
  Button,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useState } from "react";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";

import CardHeader from "../General/CardHeader";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import FileUploadBox from "../General/FileUploadBox"; 

import { uploadJudicialAccountsZip } from "../../services/JudicialAccountService";
import GlassCard from "../General/GlassCard";
import { useToast } from "../../context/ToastContext";

export default function ChargeJudicialAccount() {
  const { showToast } = useToast();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);

  const [openHelp, setOpenHelp] = useState(false);
  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  const handleFileSelect = (file) => {
    if (!file) return;

    const validExtensions = ["zip"];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setErrors({
        ...errors,
        file: "Solo se permiten archivos .zip",
      });
      setSelectedFile(null);
      return;
    }

    setErrors({ ...errors, file: "" });
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();    setErrors({});
    setLoading(true);

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo .zip.", "info")
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await uploadJudicialAccountsZip(formData);

      showToast(response.data.message, "success")
      setSelectedFile(null);
      setErrors({});
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error")
      } else {
        showToast("Ocurrió un error inesperado.", "error")
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del ZIP" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={handleOpenHelp}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AccountBalanceIcon />}
            title="Carga de Cuentas Judiciales"
            description="Subí un archivo .zip que contenga uno o varios .csv exportados desde el sistema de Justicia."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".zip"
                  placeholder="Seleccionar archivo ZIP"
                  helperText="O arrastrá el archivo de cuentas aquí (.zip)"
                />
                
                {errors.file && (
                  <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {errors.file}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={loading}
                  sx={{ fontWeight: 'bold' }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Importar Cuentas Judiciales"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={handleCloseHelp}
        title="Requisitos del archivo ZIP"
        description={
          "Debe ser un .zip que contenga uno o varios archivos .csv. " +
          "Cada CSV debe tener header y las columnas: EXPEDIENTE, CUENTA, SUCURSAL, SALDO, OBSERVACIONES."
        }
        columns={["EXPEDIENTE", "CUENTA", "SUCURSAL", "SALDO", "OBSERVACIONES"]}
        example={[
          "1228094",
          "30570100",
          "322",
          "0",
          "Saldo de la cuenta al: 05/03/2026",
        ]}
      />
    </Grid>
  );
}